import { addDays, differenceInDays } from 'date-fns';
import { BoardLetter } from '../model/IPuzzle';
import { Direction, DrawTrack } from '../redux/Slices/Game';
import { SimpleDrawTrack } from '../pages/Game/Common/Modals/Yesterday/MicroPuzzle';

const puzzleStartDate = new Date('2024-02-28T00:00:00');

export function getPuzzleIndexForDate(date: Date) {
  return differenceInDays(date, puzzleStartDate);
}

export function dateFromPuzzleIndex(index: number) {
  return addDays(puzzleStartDate, index);
}

const neighborDirs: Array<Array<number>> = [
  [-1, -1],
  [0, -1],
  [1, -1],
  [-1, 0],
  [1, 0],
  [-1, 1],
  [0, 1],
  [1, 1],
];

const getFromMatrix =
  <T>(m: Array<Array<T>>) =>
  ([x, y]: number[]) =>
    (m[y] || [])[x];

export const getNeighbors = <T>(x: number, y: number, m: Array<Array<T>>) =>
  neighborDirs
    .map(([dX, dY]) => [x + dX, y + dY])
    .map(getFromMatrix(m))
    .filter((v) => v !== undefined);

export const FindSolution = (word: string, board: BoardLetter[][]) => {
  const letters = word.split('');
  const possibleStarts = board.flat(1).filter((e) => e.label === letters[0]);

  let prev = possibleStarts.map((e) => [e]);
  let next: BoardLetter[][] = [];

  letters.slice(1, letters.length).forEach((letter) => {
    prev.forEach((chain) => {
      const last = chain[chain.length - 1];
      const neighbours = getNeighbors(last.x, last.y, board)
        .filter((c) => c.label === letter)
        .filter((c) => !chain.some((d) => d.x === c.x && d.y === c.y));

      neighbours.forEach((e) => next.push([...chain, e]));
    });
    prev = next;
    next = [];
  });

  return prev;
};

export const getDirection = (
  begin: DrawTrack | SimpleDrawTrack,
  end: DrawTrack | SimpleDrawTrack,
): Direction | undefined => {
  if (!begin || !end) return undefined;
  const checks: { x: number; y: number; result: Direction }[] = [
    { x: 0, y: -1, result: 'north' },
    { x: 1, y: -1, result: 'north-east' },
    { x: 1, y: 0, result: 'east' },
    { x: 1, y: 1, result: 'south-east' },
    { x: 0, y: 1, result: 'south' },
    { x: -1, y: 1, result: 'south-west' },
    { x: -1, y: 0, result: 'west' },
    { x: -1, y: -1, result: 'north-west' },
  ];
  for (const i of checks) {
    if (begin.x + i.x === end.x && begin.y + i.y === end.y) return i.result;
  }
  return undefined;
};

export const Directions = {
  north: 'rotate(-90deg)',
  'north-east': 'rotate(-45deg)',
  east: 'rotate(0deg)',
  'south-east': 'rotate(45deg)',
  south: 'rotate(90deg)',
  'south-west': 'rotate(135deg)',
  west: 'rotate(180deg)',
  'north-west': 'rotate(225deg)',
};

export const MapToTransform = (dir: Direction) => {
  return Directions[dir];
};

export const MapToLength = (dir: Direction, cellMode3x3: boolean) => {
  switch (dir) {
    case 'east':
    case 'north':
    case 'west':
    case 'south':
      return cellMode3x3 ? 'var(--vertical-line-3x3)' : 'var(--vertical-line)';
    case 'north-east':
    case 'north-west':
    case 'south-east':
    case 'south-west':
      return cellMode3x3 ? 'var(--diagonal-line-3x3)' : 'var(--diagonal-line)';
    default:
      return cellMode3x3 ? 'var(--vertical-line-3x3)' : 'var(--vertical-line)';
  }
};

export const MapToLengthSmall = (dir: Direction, cellMode3x3: boolean) => {
  switch (dir) {
    case 'east':
    case 'north':
    case 'west':
    case 'south':
      return cellMode3x3 ? '68px' : '51px';
    case 'north-east':
    case 'north-west':
    case 'south-east':
    case 'south-west':
      return cellMode3x3 ? '96px' : '72px';
    default:
      return cellMode3x3 ? '68px' : '51px';
  }
};

type MapElement = {
  total: number;
  first: number;
};

type SpecialIndex = `${number}-${number}`;

export interface SpecialMap {
  [key: SpecialIndex]: MapElement;
}

export const CalculateBoardStatus = (
  board: BoardLetter[][],
  words: string[],
  foundWords: string[],
) => {
  const Total: SpecialMap = {};
  board.flat(1).forEach((e) => {
    Total[`${e.x}-${e.y}`] = { total: 0, first: 0 };
  });

  const wordsLeft = words.filter((e) => !foundWords.includes(e));

  wordsLeft.forEach((word) => {
    const solution = FindSolution(word, board)[0];

    const firstLetter = solution[0];
    Total[`${firstLetter.x}-${firstLetter.y}`].total += 1;
    Total[`${firstLetter.x}-${firstLetter.y}`].first += 1;
    solution.slice(1, solution.length).forEach((letter) => {
      Total[`${letter.x}-${letter.y}`].total += 1;
    });
  });
  return Total;
};

export const RotateCoords = (
  track: SimpleDrawTrack,
  rotation: number,
  n = 4,
): DrawTrack => {
  let rotations = (rotation % 360) / 90;

  let rotated_y = track.y;
  let rotated_x = track.x;

  if (rotations > 0) {
    while (rotations > 0) {
      const x_new = n - rotated_y - 1;
      const y_new = rotated_x;
      rotated_x = x_new;
      rotated_y = y_new;
      rotations -= 1;
    }
  }

  if (rotations < 0) {
    while (rotations < 0) {
      const x_new = rotated_y;
      const y_new = n - rotated_x - 1;
      rotated_x = x_new;
      rotated_y = y_new;
      rotations += 1;
    }
  }
  return {
    x: rotated_x,
    y: rotated_y,
    label: track.label,
    pX: track.x,
    pY: track.y,
  };
};
